import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  IpoLanguageService,
  IpoTranslatePipe,
} from '@ip-only/front-end-library';
import { Subject, takeUntil } from 'rxjs';
import { Language } from '../../../../shared/language-chooser/language-chooser-entities';
import { DeliveryReportPdfService } from '../../../../shared/pdf-tools/pdf-report/delivery-report-pdf.service';
import { RolloutProgressReportEntities } from '../../../../shared/self-inspection-reports/service/entities';
import { ToastsService } from '../../../../shared/toast/toast.service';
import { KpReportStatusService } from '../../../service/kp-report-status.service';
import {
  DistributionPointProperties,
  DuctProperties,
  PointType,
} from '../../../span-progress-entities';
import {
  createPopupPdfContext,
  PopupPdfDownloadController,
} from '../popup-pdf-download.controller';
import {
  buildRolloutReportUrl,
  cancelPdfDownloadOnPointChange,
  getIfsFromUrl,
  POPUP_PDF_DOWNLOAD_TOOLTIP,
} from '../popup-pdf.utils';

@Component({
  selector: 'app-ductpoint-popup-content',
  templateUrl: './ductpoint-popup-content.component.html',
  styleUrls: ['./ductpoint-popup-content.component.scss'],
  standalone: false,
})
export class DuctpointPopupContentComponent implements OnChanges, OnDestroy {
  @Input() properties: DistributionPointProperties;
  @Input() initialExpandedPointId?: string;
  @Output() dismiss = new EventEmitter();

  multiDuctProperties: DuctProperties | null = null;
  subDuctProperties: DuctProperties | null = null;

  readonly ReportStatus = RolloutProgressReportEntities.v3.ReportStatus;
  private reportStatusCancel$ = new Subject<void>();

  readonly pointStatuses = signal<
    Record<string, RolloutProgressReportEntities.v3.ReportStatus>
  >({});

  isAggregated = false;
  downloadTooltipLabel = POPUP_PDF_DOWNLOAD_TOOLTIP;
  expandedPointId: string | null = null;
  sortedAggregatedReferences: DistributionPointProperties[] = [];
  pdfController: PopupPdfDownloadController;

  private destroyed = false;

  constructor(
    private readonly router: Router,
    private readonly pdfService: DeliveryReportPdfService,
    private readonly languageService: IpoLanguageService,
    private readonly toastsService: ToastsService,
    private readonly translate: IpoTranslatePipe,
    private readonly kpReportStatusService: KpReportStatusService,
  ) {
    this.pdfController = new PopupPdfDownloadController(
      pdfService,
      toastsService,
      translate,
      createPopupPdfContext(
        (point?: DistributionPointProperties) => {
          const p = point ?? this.properties;
          const ifsNumber = getIfsFromUrl(this.router.url);
          const deliveryOrderId = this.getConnectedHpLeadId(p);
          if (!ifsNumber || !deliveryOrderId) return null;
          return {
            ifsNumber,
            deliveryOrderId,
            locale:
              (this.languageService.getStoredLanguage() as Language) ?? 'en',
            address: p.name ?? '-',
            rolloutPointType: RolloutProgressReportEntities.v3.MarkerTypes.HP,
          };
        },
        () => this.destroyed,
      ),
    );
  }

  ngOnChanges(changes?: SimpleChanges): void {
    cancelPdfDownloadOnPointChange(
      changes,
      (prev, curr) =>
        this.getPointIdentity(prev as DistributionPointProperties) !==
        this.getPointIdentity(curr as DistributionPointProperties),
      () => this.pdfController.cleanup(),
    );

    this.isAggregated =
      (this.properties.aggregatedMarkerReferences?.length ?? 0) > 1;
    this.multiDuctProperties = this.properties.duct ?? null;
    this.subDuctProperties = this.properties.subDuct ?? null;

    if (this.isAggregated) {
      this.expandedPointId = this.initialExpandedPointId ?? this.properties.id;
      const refs = this.properties.aggregatedMarkerReferences ?? [];
      const mainId = this.properties.id;
      this.sortedAggregatedReferences = [...refs].sort((a, b) => {
        if (a.id === mainId) return -1;
        if (b.id === mainId) return 1;
        return 0;
      });
    } else {
      this.sortedAggregatedReferences = [];
    }
    this.loadReportStatuses();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.pdfController.cleanup();
    this.reportStatusCancel$.next();
    this.reportStatusCancel$.complete();
  }

  private loadReportStatuses(): void {
    this.pointStatuses.set({});

    const ifsNumber = getIfsFromUrl(this.router.url);
    if (!ifsNumber) {
      return;
    }

    this.kpReportStatusService
      .getPointStatuses(ifsNumber, this.properties)
      .pipe(takeUntil(this.reportStatusCancel$))
      .subscribe((statuses) => {
        this.pointStatuses.set(statuses);
      });
  }

  private getPointIdentity(point: DistributionPointProperties): string {
    return this.getConnectedHpLeadId(point);
  }

  togglePoint(id: string): void {
    this.expandedPointId = this.expandedPointId === id ? null : id;
  }

  isPointExpanded(id: string): boolean {
    return this.expandedPointId === id;
  }

  isKp(point: DistributionPointProperties): boolean {
    return point.markerTypeId === PointType.DuctPoint;
  }

  isKpWithLeadIdPresent(point: DistributionPointProperties): boolean {
    return (
      this.isKp(point) &&
      this.kpReportStatusService.getLinkedHpId(point) != null
    );
  }

  private getConnectedHpLeadId(point: DistributionPointProperties): string {
    return this.kpReportStatusService.getLinkedHpId(point) ?? point.sourceKey;
  }

  openReport(point: DistributionPointProperties): void {
    window.open(
      buildRolloutReportUrl(
        this.router.url,
        RolloutProgressReportEntities.v3.MarkerTypes.HP,
        this.getConnectedHpLeadId(point),
      ),
      '_blank',
    );
  }

  onDownloadPreviewPdf(
    point: DistributionPointProperties,
    event: MouseEvent,
  ): void {
    this.pdfController.onDownloadPreviewPdf(event, point);
  }

  onDownloadPreviewPdfKeyDown(
    point: DistributionPointProperties,
    event: KeyboardEvent,
  ): void {
    this.pdfController.onDownloadPreviewPdfKeyDown(event, point);
  }

  onClosePopup(): void {
    this.dismiss.emit();
  }
}
